import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Scale, AlertCircle, CheckCircle } from 'lucide-react'
import SEO, { schemas } from '../components/SEO'
import { useLocale } from '../i18n/Link'

const LEGAL_EMAIL = 'legal@cmdesk.help'
const UPDATED_AT = new Date(2026, 2, 28)

export default function Terms() {
  const { t } = useTranslation(['terms', 'common'])
  const { htmlLang } = useLocale()
  const updatedAt = UPDATED_AT.toLocaleDateString(htmlLang, { day: 'numeric', month: 'long', year: 'numeric' })
  const accountItems = t('sections.account.items', { returnObjects: true }) as string[]
  const prohibitedItems = t('sections.prohibited.items', { returnObjects: true }) as string[]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}

      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/terms"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.terms'), url: '/terms' }])}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Scale className="w-4 h-4" />
            {t('hero.eyebrow')}
          </span>
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
            {t('hero.title')}
          </h1>
          <p className="text-gray-400 text-lg">
            {t('hero.updated', { date: updatedAt })}
          </p>
        </motion.div>

        <div className="space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-4">{t('sections.acceptance.title')}</h2>
            <p className="text-gray-400 leading-relaxed">
              {t('sections.acceptance.text')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-4">{t('sections.account.title')}</h2>
            <p className="text-gray-400 leading-relaxed mb-4">
              {t('sections.account.intro')}
            </p>
            <ul className="space-y-2 text-gray-400">
              {accountItems.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-4">{t('sections.prohibited.title')}</h2>
            <p className="text-gray-400 leading-relaxed mb-4">
              {t('sections.prohibited.intro')}
            </p>
            <ul className="space-y-2 text-gray-400">
              {prohibitedItems.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-4">{t('sections.billing.title')}</h2>
            <p className="text-gray-400 leading-relaxed">
              {t('sections.billing.text')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-4">{t('sections.disclaimer.title')}</h2>
            <p className="text-gray-400 leading-relaxed">
              {t('sections.disclaimer.text')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-4">{t('sections.contact.title')}</h2>
            <p className="text-gray-400 leading-relaxed">
              {t('sections.contact.text', { email: LEGAL_EMAIL })}
            </p>
          </motion.div>
        </div>
      </div>
    </motion.div>
  )
}
